/* Owns every HTTP call the panel makes: URL building, the bearer header, the
   `{ error: { code, message, details } }` envelope → `ApiHttpError` translation,
   and the single-flight access-token refresh. Feature `api.ts` modules are the
   only callers; pages never touch it directly. */

import { ERROR_STATUS, type ErrorCode } from '@sentinelac/shared'

import { clearAuth, getAccessToken, setAccessToken, setUser } from './auth-store.js'

/**
 * nginx exposes the API at `/api/` and strips the prefix, so the browser calls
 * `/api/v1/...` while the Fastify route is `/v1/...` (API.md §1.1). In dev the
 * Vite proxy does the same rewrite.
 */
const API_BASE: string = (import.meta.env['VITE_API_BASE'] as string | undefined) ?? '/api'
export const API_ROOT = `${API_BASE}/v1`

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'

export type QueryValue = string | number | boolean | null | undefined | Array<string | number | boolean>
export type QueryParams = Record<string, QueryValue>

export interface ApiRequestInit {
  method?: HttpMethod
  body?: unknown
  query?: QueryParams
  signal?: AbortSignal
  /** API.md §1.7 — only `POST …/commands` and `POST …/bans` honour it. */
  idempotencyKey?: string
  headers?: Record<string, string>
}

/** The one error type every panel surface keys off, carrying the frozen `ErrorCode`. */
export class ApiHttpError extends Error {
  readonly code: ErrorCode
  readonly status: number
  readonly details: Record<string, unknown> | undefined
  readonly reqId: string | undefined
  /** Seconds advertised by `retry-after`, when the server sent one. */
  readonly retryAfterSec: number | undefined

  constructor(input: {
    code: ErrorCode
    message: string
    status: number
    details?: Record<string, unknown>
    reqId?: string
    retryAfterSec?: number
  }) {
    super(input.message)
    this.name = 'ApiHttpError'
    this.code = input.code
    this.status = input.status
    this.details = input.details
    this.reqId = input.reqId
    this.retryAfterSec = input.retryAfterSec
  }

  static is(value: unknown): value is ApiHttpError {
    return value instanceof ApiHttpError
  }

  /** True for the codes that mean "this request will never succeed as sent". */
  get isClientError(): boolean {
    return this.status >= 400 && this.status < 500
  }
}

function isErrorCode(value: unknown): value is ErrorCode {
  return typeof value === 'string' && Object.prototype.hasOwnProperty.call(ERROR_STATUS, value)
}

export function buildQuery(query: QueryParams | undefined): string {
  if (!query) return ''
  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === '') continue
    // Repeatable filters (`detectionKey`, `severity`, `action`) OR-combine
    // inside their own group and travel as repeated keys (API.md §1.5).
    if (Array.isArray(value)) {
      for (const entry of value) params.append(key, String(entry))
    } else {
      params.append(key, String(value))
    }
  }
  const qs = params.toString()
  return qs ? `?${qs}` : ''
}

// ─────────────────────────── session lifecycle hooks ──────────────────────────

type SessionLostHandler = () => void
type TokenRefreshedHandler = (token: string) => void

let onSessionLost: SessionLostHandler = () => {
  // Fallback for the pre-router boot window; the router replaces it in main.tsx.
  if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
    const next = encodeURIComponent(window.location.pathname + window.location.search)
    window.location.assign(`/login?next=${next}`)
  }
}

const tokenRefreshedHandlers = new Set<TokenRefreshedHandler>()

/** The router installs a navigate-based handler so a lost session does not full-reload the app. */
export function setSessionLostHandler(handler: SessionLostHandler): void {
  onSessionLost = handler
}

/** `lib/ws.ts` subscribes so it can send `{op:'auth', token}` instead of tearing the socket down. */
export function onAccessTokenRefreshed(handler: TokenRefreshedHandler): () => void {
  tokenRefreshedHandlers.add(handler)
  return () => {
    tokenRefreshedHandlers.delete(handler)
  }
}

function announceRefreshedToken(token: string): void {
  for (const handler of tokenRefreshedHandlers) handler(token)
}

// ───────────────────────────── single-flight refresh ──────────────────────────

/**
 * Module-level, deliberately not React state: three tabs' worth of concurrent
 * 401s must produce exactly ONE `POST /v1/auth/refresh`. The API rotates the
 * refresh-token family on every call and treats a replayed token as reuse
 * (`AUTH_REFRESH_REUSED`, whole family revoked), so a refresh storm would log
 * the user out rather than merely waste requests.
 */
let refreshInFlight: Promise<string> | null = null

interface AuthResponseShape {
  accessToken: string
  expiresIn: number
  user: Parameters<typeof setUser>[0]
}

async function performRefresh(): Promise<string> {
  const response = await fetch(`${API_ROOT}/auth/refresh`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: '{}',
  })
  if (!response.ok) {
    throw await toApiError(response)
  }
  const data = (await response.json()) as AuthResponseShape
  setAccessToken(data.accessToken)
  setUser(data.user)
  announceRefreshedToken(data.accessToken)
  return data.accessToken
}

/** Awaits the in-flight refresh when there is one, otherwise starts it. */
export function refreshAccessToken(): Promise<string> {
  if (refreshInFlight) return refreshInFlight
  const promise = performRefresh()
  refreshInFlight = promise
  // Clearing in a `.finally` on the stored promise (not on what callers receive)
  // keeps every concurrent caller attached to this one attempt while still
  // letting a later, genuinely new 401 start a fresh refresh.
  void promise.finally(() => {
    if (refreshInFlight === promise) refreshInFlight = null
  })
  return promise
}

/** Test seam — the module singleton would otherwise leak a pending refresh between cases. */
export function resetRefreshStateForTests(): void {
  refreshInFlight = null
}

// ──────────────────────────────── the request path ────────────────────────────

function readReqId(response: Response): string | undefined {
  return response.headers.get('x-request-id') ?? response.headers.get('x-req-id') ?? undefined
}

async function toApiError(response: Response): Promise<ApiHttpError> {
  let code: ErrorCode = 'INTERNAL'
  let message = response.statusText || `HTTP ${response.status}`
  let details: Record<string, unknown> | undefined

  try {
    const body: unknown = await response.json()
    if (body && typeof body === 'object' && 'error' in body) {
      const envelope = (body as { error: unknown }).error
      if (envelope && typeof envelope === 'object') {
        const e = envelope as { code?: unknown; message?: unknown; details?: unknown }
        if (isErrorCode(e.code)) code = e.code
        if (typeof e.message === 'string') message = e.message
        if (e.details && typeof e.details === 'object') details = e.details as Record<string, unknown>
      }
    }
  } catch {
    // A non-JSON body (nginx 502 page, aborted stream) still has to produce a
    // typed error, so the panel never renders a raw HTML fragment.
  }

  const retryAfterHeader = response.headers.get('retry-after')
  const retryAfterSec = retryAfterHeader === null ? undefined : Number(retryAfterHeader)

  return new ApiHttpError({
    code,
    message,
    status: response.status,
    details,
    reqId: readReqId(response),
    retryAfterSec: Number.isFinite(retryAfterSec) ? retryAfterSec : undefined,
  })
}

function isAuthPath(path: string): boolean {
  // The `sac_rt` cookie is `Path=/api/v1/auth`, so it can only ever be attached
  // here anyway; sending credentials for the whole group keeps login's
  // `Set-Cookie` working when the panel is served from another origin in dev.
  return path.startsWith('/auth/')
}

function buildRequestInit(path: string, init: ApiRequestInit, token: string | null): RequestInit {
  const headers: Record<string, string> = { accept: 'application/json', ...init.headers }
  if (init.body !== undefined) headers['content-type'] = 'application/json'
  if (token) headers['authorization'] = `Bearer ${token}`
  if (init.idempotencyKey) headers['idempotency-key'] = init.idempotencyKey

  const requestInit: RequestInit = {
    method: init.method ?? 'GET',
    headers,
  }
  if (init.body !== undefined) requestInit.body = JSON.stringify(init.body)
  if (init.signal) requestInit.signal = init.signal
  if (isAuthPath(path)) requestInit.credentials = 'include'
  return requestInit
}

const sleep = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms))

interface Attempt {
  refreshed: boolean
  rateLimitRetried: boolean
}

async function send(path: string, init: ApiRequestInit, attempt: Attempt): Promise<Response> {
  const url = `${API_ROOT}${path}${buildQuery(init.query)}`
  const response = await fetch(url, buildRequestInit(path, init, getAccessToken()))
  if (response.ok) return response

  const error = await toApiError(response)

  // 401: refresh once (single-flight) and replay the original request exactly
  // once. A second 401, or reuse detection, is unrecoverable.
  if (
    response.status === 401 &&
    !attempt.refreshed &&
    !path.startsWith('/auth/refresh') &&
    error.code !== 'AUTH_REFRESH_REUSED'
  ) {
    try {
      await refreshAccessToken()
    } catch {
      clearAuth()
      onSessionLost()
      throw error
    }
    return send(path, init, { ...attempt, refreshed: true })
  }

  if (response.status === 401 || error.code === 'AUTH_REFRESH_REUSED') {
    clearAuth()
    onSessionLost()
    throw error
  }

  // API.md §1.6 — a 429 is retried at most once, after the advertised delay,
  // and only for GET. Every other 4xx is final: retrying a validation error or
  // a 403 only burns the caller's rate-limit budget.
  const method = init.method ?? 'GET'
  if (response.status === 429 && method === 'GET' && !attempt.rateLimitRetried) {
    const retryAfterMs =
      typeof error.details?.['retryAfterMs'] === 'number'
        ? (error.details['retryAfterMs'] as number)
        : (error.retryAfterSec ?? 1) * 1000
    await sleep(Math.min(retryAfterMs, 15_000))
    return send(path, init, { ...attempt, rateLimitRetried: true })
  }

  throw error
}

/** The typed JSON call. `T` is the DTO from `@sentinelac/shared`; nothing is re-declared here. */
export async function api<T>(path: string, init: ApiRequestInit = {}): Promise<T> {
  const response = await send(path, init, { refreshed: false, rateLimitRetried: false })
  if (response.status === 204) return undefined as T
  const text = await response.text()
  if (!text) return undefined as T
  return JSON.parse(text) as T
}

/** Binary reads (screenshot bytes, CSV and JSON exports) that still need the bearer token. */
export async function apiBlob(
  path: string,
  init: ApiRequestInit = {},
): Promise<{ blob: Blob; filename: string | null }> {
  const response = await send(path, init, { refreshed: false, rateLimitRetried: false })
  const disposition = response.headers.get('content-disposition')
  const match = disposition ? /filename="?([^";]+)"?/.exec(disposition) : null
  return { blob: await response.blob(), filename: match?.[1] ?? null }
}

/** `crypto.randomUUID` is available in every browser the panel targets and in jsdom ≥ 22. */
export function newIdempotencyKey(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID()
  return `idem-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
}
